import React from 'react';
import { Icon } from '@iconify/react';
import { LoyaltySummaryResponse } from '@/types/loyalty';

interface Props {
  data: LoyaltySummaryResponse | null;
  loading: boolean;
  onRefresh: () => void;
}

export const SummaryTab: React.FC<Props> = ({ data, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 rounded-[16px] bg-[#F6F6F6] animate-pulse" />
          ))}
        </div>
        <div className="h-64 rounded-[16px] bg-[#F6F6F6] animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-[16px] border border-dashed border-[#D5D5D5] bg-white p-12 text-center">
        <Icon icon="ph:chart-bar" className="w-10 h-10 text-[#8C8C8C] mx-auto mb-2" />
        <h4 className="text-[16px] font-bold text-[#0E1726]">Belum ada data ringkasan</h4>
        <p className="text-[13px] text-[#8C8C8C] mt-1">
          Data performa loyalty akan muncul setelah ada aktivitas transaksi pelanggan.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 4 Metric Cards per Sporto Design System */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.cards.map((card, idx) => (
          <div
            key={idx}
            className="rounded-[16px] border border-[#E0E6ED] bg-white py-[18px] px-5 shadow-none hover:border-[#D5D5D5] transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[13px] font-medium text-[#8C8C8C]">{card.title}</span>
              <Icon icon={card.icon ? card.icon.replace('-bold', '') : 'ph:trend-up'} className="w-4 h-4 text-[#8C8C8C]" />
            </div>
            <div className="text-[24px] font-semibold text-[#0E1726] font-['Inter'] tracking-[-0.4px]">
              {card.value}
            </div>
            <p className="text-[12px] text-[#8C8C8C] mt-1.5">{card.subtitle}</p>
          </div>
        ))}
      </div>

      {/* Follow-up Section Panel */}
      <div className="rounded-[16px] border border-[#E0E6ED] bg-white p-6 shadow-none">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-5 border-b border-[#E0E6ED] gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h3 className="text-[16px] font-bold text-[#0E1726]">Daftar Follow-up Pelanggan</h3>
              <span className="inline-flex items-center rounded-full bg-[#FFF5EE] px-2.5 py-0.5 text-[12px] font-medium text-[#FC480D] border border-[#FC480D]">
                {data.follow_up_list?.length || 0} Perlu Tindakan
              </span>
            </div>
            <p className="text-[13px] text-[#8C8C8C] mt-1">
              Hubungi pelanggan yang mendekati batas reward atau masa aktif keanggotaan via WhatsApp.
            </p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            className="h-10 px-4 rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6] text-[14px] font-semibold inline-flex items-center gap-2 transition-colors self-start sm:self-auto"
          >
            <Icon icon="ph:arrows-clockwise" className="w-4 h-4 text-[#8C8C8C]" />
            Refresh
          </button>
        </div>

        {/* Follow-up Items */}
        {!data.follow_up_list || data.follow_up_list.length === 0 ? (
          <div className="py-14 text-center">
            <Icon icon="ph:check-circle" className="w-10 h-10 text-[#8C8C8C] mx-auto mb-2" />
            <p className="text-[14px] font-semibold text-[#1F1F1F]">Semua pelanggan terkelola dengan baik</p>
            <p className="text-[12px] text-[#8C8C8C] mt-0.5">
              Belum ada pelanggan yang perlu difollow-up saat ini.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#E0E6ED]">
            {data.follow_up_list.map((item, idx) => (
              <div
                key={idx}
                className="py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-[#F6F6F6] px-3 rounded-[12px] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F6F6F6] text-[#8C8C8C] flex items-center justify-center text-[12px] font-semibold shrink-0">
                    {item.customer_name ? item.customer_name.slice(0, 2).toUpperCase() : 'PL'}
                  </div>
                  <div>
                    <h5 className="text-[13px] font-semibold text-[#0E1726]">{item.customer_name}</h5>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[12px] text-[#8C8C8C] font-mono">
                        {item.customer_phone || '-'}
                      </span>
                      <span className="text-[#D5D5D5]">•</span>
                      <span className="text-[12px] font-medium text-[#FC480D] bg-[#FFF5EE] px-2 py-0.5 rounded-full border border-[#FC480D]/40">
                        {item.status_note}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Secondary Pill Button 32px height */}
                <a
                  href={item.whatsapp_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 h-8 px-3.5 rounded-full bg-[#FFF5EE] hover:bg-[#ffe8db] text-[#FC480D] text-[13px] font-semibold transition-colors shrink-0"
                >
                  <Icon icon="ph:whatsapp-logo" className="w-4 h-4 text-[#FC480D]" />
                  Hubungi WhatsApp
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryTab;
